import apiClient from '../apiClient';

export interface MultilingualField {
  en: string;
  ar?: string;
}

export type TemplateCategory =
  | 'history-physical'
  | 'progress-note'
  | 'procedure-note'
  | 'discharge-summary'
  | 'consultation-note'
  | 'nursing-assessment'
  | 'custom';

// Specialty taxonomy is shared with users + protocols (see data/specialties.ts).
// Keep the alias here so existing call sites (TemplateField, TemplateSettings,
// TemplateLibraryPage) keep importing `TemplateSpecialty` from this module.
export type TemplateSpecialty = string;

export interface TemplateField {
  id: string;
  name: MultilingualField;
  type:
    | 'text'
    | 'textarea'
    | 'number'
    | 'date'
    | 'select'
    | 'checkbox'
    | 'radio'
    | 'section'
    | 'table'
    | 'tags';
  required?: boolean;
  defaultValue?: unknown;
  options?: Array<{
    value: string;
    label: MultilingualField;
  }>;
  validation?: {
    min?: number;
    max?: number;
    maxLength?: number;
    pattern?: string;
    message?: MultilingualField;
  };
  placeholder?: MultilingualField;
  helpText?: MultilingualField;
  autoPopulate?: {
    source: 'patient' | 'vitals' | 'medications' | 'allergies' | 'history';
    field: string;
  };
  rows?: number;
  tableConfig?: {
    rows?: number;
    cols?: number;
    withHeaderRow?: boolean;
  };
  children?: TemplateField[];
  columns?: TemplateField[];
}

export interface TemplateSection {
  id: string;
  title: MultilingualField;
  fields: TemplateField[];
  collapsible?: boolean;
  defaultExpanded?: boolean;
}

export interface TemplateMacro {
  id: string;
  trigger: string;
  replacement: MultilingualField;
  category?: string;
}

export interface Template {
  id: string;
  name: MultilingualField;
  description?: MultilingualField;
  category: TemplateCategory;
  specialty: TemplateSpecialty;
  sections: TemplateSection[];
  version: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  lastModifiedBy?: string;
  isActive: boolean;
  isPublic: boolean;
  /** Slice A: at most one default per (tenant, specialty). */
  isDefaultForSpecialty?: boolean;
  tags?: string[];
  macros?: TemplateMacro[];
  requiredRoles?: string[];
  creator?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  modifier?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export interface CreateTemplateDto {
  name: MultilingualField;
  description?: MultilingualField;
  category: TemplateCategory;
  specialty: TemplateSpecialty;
  sections: TemplateSection[];
  version?: number;
  isActive?: boolean;
  isPublic?: boolean;
  isDefaultForSpecialty?: boolean;
  tags?: string[];
  macros?: TemplateMacro[];
  requiredRoles?: string[];
}

export type UpdateTemplateDto = Partial<CreateTemplateDto>;

export interface FilterTemplateDto {
  category?: TemplateCategory;
  specialty?: TemplateSpecialty;
  search?: string;
  isActive?: boolean;
  isPublic?: boolean;
  createdBy?: string;
  tags?: string;
  page?: number;
  limit?: number;
}

export const templatesApi = {
  getTemplates: async (filters: FilterTemplateDto = {}) => {
    const params = new URLSearchParams();

    if (filters.category) params.append('category', filters.category);
    if (filters.specialty) params.append('specialty', filters.specialty);
    if (filters.search) params.append('search', filters.search);
    if (filters.isActive !== undefined)
      params.append('isActive', filters.isActive.toString());
    if (filters.isPublic !== undefined)
      params.append('isPublic', filters.isPublic.toString());
    if (filters.createdBy) params.append('createdBy', filters.createdBy);
    if (filters.tags) params.append('tags', filters.tags);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await apiClient.get<{
      success: boolean;
      data: {
        data: Template[];
        total: number;
        page: number;
        limit: number;
      };
    }>(`/templates?${params.toString()}`);

    return response.data;
  },

  getTemplateById: async (id: string) => {
    const response = await apiClient.get<{
      success: boolean;
      data: Template;
    }>(`/templates/${id}`);

    return response.data;
  },

  createTemplate: async (data: CreateTemplateDto) => {
    const response = await apiClient.post<{
      success: boolean;
      data: Template;
    }>('/templates', data);

    return response.data;
  },

  updateTemplate: async (id: string, data: UpdateTemplateDto) => {
    const response = await apiClient.patch<{
      success: boolean;
      data: Template;
    }>(`/templates/${id}`, data);

    return response.data;
  },

  deleteTemplate: async (id: string) => {
    const response = await apiClient.delete<{
      success: boolean;
    }>(`/templates/${id}`);

    return response.data;
  },

  /**
   * Promote this template to the tenant's default for its specialty.
   * Atomic on the server — clears the flag on any sibling that currently
   * holds it, so the partial-unique index isn't tripped.
   */
  setDefaultForSpecialty: async (id: string) => {
    const response = await apiClient.patch<{
      success: boolean;
      data: Template;
    }>(`/templates/${id}/default`);

    return response.data;
  },

  duplicateTemplate: async (id: string) => {
    const response = await apiClient.post<{
      success: boolean;
      data: Template;
    }>(`/templates/${id}/duplicate`);

    return response.data;
  },

  mapScribeToTemplate: async (
    templateId: string,
    scribeSummary: Record<string, unknown>,
    language: string = 'en',
    transcription?: string,
  ) => {
    const payload: Record<string, unknown> = { language };
    if (transcription) payload.transcription = transcription;
    if (Object.keys(scribeSummary).length > 0) payload.scribeSummary = scribeSummary;

    const response = await apiClient.post<{
      field_values: Record<string, unknown>;
      unmapped_fields: string[];
    }>(`/templates/${templateId}/map-scribe`, payload);

    return response.data;
  },
};
